/**
 * PatientRecoveryTile — Iter 82.
 *
 * Shows the patient their own NPRS / VAS-based recovery % — the SAME metric
 * the referring GP sees on AdminGpEarnings. The tile renders even when the
 * patient has < 2 outcomes recorded, with a friendly empty-state message.
 *
 * Sharable: a single tap copies a humane summary to the clipboard ("My pain
 * dropped from 7 → 2 over 12 weeks · 71% better · tracked at Physiogenic")
 * that the patient can paste into iMessage / WhatsApp / X.
 */
import React, { useEffect, useState } from 'react';
import { Activity, Share2, TrendingDown, CheckCircle2, Info, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { copyToClipboard } from '../lib/clipboard';
import { t } from '../lib/i18n';
import { useAuth } from '../lib/auth';

export default function PatientRecoveryTile() {
  const { locale } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let stop = false;
    api.get('/patients/me/recovery')
      .then((r) => { if (!stop) setD(r.data); })
      .catch(() => { if (!stop) setD(null); })
      .finally(() => { if (!stop) setLoading(false); });
    return () => { stop = true; };
  }, []);

  if (loading) {
    return (
      <div className="bento-card p-4 flex items-center gap-2 text-fg-muted text-sm" data-testid="patient-recovery-loading">
        <Loader2 className="w-4 h-4 animate-spin"/> Loading your recovery…
      </div>
    );
  }
  if (!d) return null;

  const pct = d.recovery_rate_pct;
  const recovered = d.recovered;
  const samples = d.samples || 0;
  const insufficient = samples < 2;

  const share = async () => {
    if (insufficient || pct == null) return;
    const text = `${t(locale, 'your_recovery')}: ${d.initial} → ${d.latest} · ${pct}% ${t(locale, 'better')} · Physiogenic`;
    try {
      if (navigator.share) {
        await navigator.share({ title: t(locale, 'your_recovery'), text });
      } else {
        await copyToClipboard(text, { label: 'Copied — paste it anywhere.' });
      }
    } catch (_e) { /* user-cancelled — silent */ }
  };

  return (
    <div className="bento-card p-5" data-testid="patient-recovery-tile">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-accent font-medium">
            {t(locale, 'your_recovery')}
          </div>
          <h3 className="font-heading text-lg font-bold flex items-center gap-2 mt-1">
            <Activity className="w-5 h-5 text-brand"/> {t(locale, 'pain_label')}
          </h3>
        </div>
        {!insufficient && pct != null && (
          <button onClick={share}
                  className="text-xs inline-flex items-center gap-1.5 text-brand hover:underline"
                  data-testid="patient-recovery-share">
            <Share2 className="w-3.5 h-3.5"/> {t(locale, 'share')}
          </button>
        )}
      </div>

      {insufficient ? (
        <div className="text-sm text-fg-muted flex items-start gap-2" data-testid="patient-recovery-empty">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0"/>
          <div>{t(locale, 'recovery_empty')}</div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-3" data-testid="patient-recovery-numbers">
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-wider text-fg-muted">{t(locale, 'start')}</div>
              <div className="text-2xl font-bold font-mono">{d.initial}</div>
            </div>
            <div className="text-center text-fg-muted self-end pb-2">
              <TrendingDown className="w-5 h-5 mx-auto"/>
            </div>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-wider text-fg-muted">{t(locale, 'now')}</div>
              <div className="text-2xl font-bold font-mono text-brand">{d.latest}</div>
            </div>
          </div>
          <div className="relative h-2 bg-bg-secondary/60 rounded-full overflow-hidden mb-3">
            <div className="absolute inset-y-0 left-0 rounded-full transition-all"
                 style={{
                   width: `${Math.max(0, Math.min(100, pct || 0))}%`,
                   background: recovered
                     ? 'linear-gradient(90deg, #1B5E20, #4CAF50)'
                     : 'linear-gradient(90deg, #003D80, #5C8DCB)',
                 }}/>
          </div>
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-1.5"
                 data-testid="patient-recovery-pct">
              <span className="text-3xl font-bold font-mono text-brand">{pct}%</span>
              <span className="text-fg-muted">{t(locale, 'better')}</span>
            </div>
            {recovered ? (
              <span className="inline-flex items-center gap-1 text-[#1B5E20] text-xs font-semibold"
                    data-testid="patient-recovery-badge">
                <CheckCircle2 className="w-4 h-4"/> {t(locale, 'recovered')}
              </span>
            ) : (
              <span className="text-xs text-fg-muted" data-testid="patient-recovery-progress">
                {t(locale, 'in_progress')} · {samples} {t(locale, 'checkins')}
              </span>
            )}
          </div>
          <p className="text-[11px] text-fg-muted mt-3">
            {t(locale, 'recovery_help', { n: d.threshold_points })}
          </p>
        </>
      )}
    </div>
  );
}
